import { Link, createFileRoute } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { NamedIcon } from "~/components/ui/named-icon";
import { getPublishedChallengesFn } from "~/lib/functions/cih/challenges";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

const CANONICAL_URL = `${BRAND_URL}/coaching-for`;

export const Route = createFileRoute("/_public/coaching-for/")({
  staticData: { title: "Guidance" },
  head: () => ({
    ...createBrandMeta({
      title: "Guidance for What You're Facing | Dream Coach Match",
      description:
        "Guidance for career transitions, relationships, health, identity, and more — frameworks, articles from coaches, and coaching for life's biggest challenges.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Guidance", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  loader: async () => {
    const challenges = await getPublishedChallengesFn();
    return { challenges };
  },
  component: CIHHubPage,
});

type Challenge = {
  id: string;
  title: string;
  slug: string | null;
  subtitle: string | null;
  description: string;
  tagline: string | null;
  icon: string | null;
  frameworks: number;
  articles: number;
};

function CIHHubPage() {
  const { challenges } = Route.useLoaderData();
  const linked = challenges.filter(
    (c): c is Challenge & { slug: string } => c.slug != null,
  );

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-14 md:pt-8 md:pb-20 xl:px-0">
          <Breadcrumb
            items={[{ label: "Home", href: "/" }, { label: "Guidance" }]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-violet-700 uppercase">
            ● Coaching Intelligence
          </div>
          <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl lg:text-6xl xl:text-7xl">
            Get guidance on what you're facing
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-normal text-gray-700">
            Coaching is one path. Explore frameworks, articles, and expert
            advice for life's biggest challenges.
          </p>
        </div>
      </section>

      {linked.length > 0 ? (
        <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
            {linked.map((challenge) => (
              <Link
                key={challenge.id}
                to="/coaching-for/$challengeSlug"
                params={{ challengeSlug: challenge.slug }}
                className="group flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-7 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md md:p-8"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-50 transition-colors group-hover:bg-violet-100">
                  <NamedIcon
                    name={challenge.icon ?? "sparkles"}
                    className="h-5 w-5 text-violet-700"
                  />
                </div>
                <h3 className="font-heading text-xl font-medium tracking-tight text-gray-900 md:text-2xl">
                  {challenge.title}
                </h3>
                {challenge.tagline || challenge.description ? (
                  <p className="text-sm leading-relaxed text-gray-500">
                    {challenge.tagline ?? challenge.description}
                  </p>
                ) : null}
                {challenge.frameworks > 0 || challenge.articles > 0 ? (
                  <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                    {[
                      challenge.frameworks > 0
                        ? `${challenge.frameworks} ${challenge.frameworks === 1 ? "framework" : "frameworks"}`
                        : null,
                      challenge.articles > 0
                        ? `${challenge.articles} ${challenge.articles === 1 ? "article" : "articles"}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                ) : null}
                <span className="mt-auto pt-3 text-sm font-medium text-gray-900">
                  Explore{" "}
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
                    →
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <CihClosingCta
        headline="Ready to start?"
        sub="A few minutes of reflection can change everything."
        cta={
          <Link to="/intake">
            <Button variant="primary" size="lg">
              Take the intake →
            </Button>
          </Link>
        }
      />
    </>
  );
}
